import { useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { isSubscriptionExpired } from '@/lib/plans';

export default function RequireAuth({ children }: { children: ReactNode }) {
  const { session, profile, loading, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !session) {
      navigate('/login', { replace: true });
    }
  }, [loading, session, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <span className="text-sm text-foreground-500">확인 중...</span>
        </div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  if (!profile || profile.status === 'pending' || profile.status === 'rejected') {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-background-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
            <i className="ri-time-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">
            {profile?.status === 'rejected' ? '가입이 거절되었습니다' : '승인 대기 중'}
          </h1>
          <p className="text-foreground-500 text-sm leading-relaxed mb-6">
            {profile?.status === 'rejected'
              ? '운영자에게 문의해주세요.'
              : '운영자가 가입을 승인하는 중입니다. 승인은 영업일 기준 24시간 이내에 처리됩니다.'}
          </p>
          <button
            onClick={() => signOut()}
            className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer text-sm"
          >
            로그아웃
          </button>
        </div>
      </div>
    );
  }

  const isPaid = profile.role === 'member' || profile.role === 'owner';
  const isStudent = profile.role === 'student';
  const expired =
    (isPaid || isStudent) &&
    (profile.status === 'expired' || isSubscriptionExpired(profile.subscription_expires_at));

  if (expired) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl border border-background-200 p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-red-100 text-red-600 mb-4">
            <i className="ri-alert-line text-2xl"></i>
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground-950 mb-2">
            이용기간이 만료되었습니다
          </h1>
          <p className="text-foreground-500 text-sm leading-relaxed mb-6">
            {isStudent
              ? '소속 학원의 이용기간이 만료되었습니다. 학원장에게 문의해주세요.'
              : '이용권을 연장하시면 다시 이용하실 수 있습니다.'}
          </p>
          <div className="flex flex-col gap-2">
            {!isStudent && (
              <button
                onClick={() => navigate('/dashboard')}
                className="whitespace-nowrap bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer text-sm"
              >
                대시보드에서 연장하기
              </button>
            )}
            <button
              onClick={() => signOut()}
              className="whitespace-nowrap border border-background-300 text-foreground-600 hover:bg-background-100 font-semibold px-6 py-2.5 rounded-md transition-colors cursor-pointer text-sm"
            >
              로그아웃
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}